"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { FadeIn } from "~/components/FadeIn/FadeIn";
import { LocationPanel } from "~/components/LocationPanel/LocationPanel";
import { Modal } from "~/components/Modal/Modal";
import { OptionButton } from "~/components/OptionButton/OptionButton";
import { Stack } from "~/components/Stack/Stack";
import { StoreHours } from "~/components/StoreHours/StoreHours";
import { Text } from "~/components/Text/Text";
import { useCart } from "~/hooks/cart";
import { type HoursResult, useHours } from "~/hooks/hours";
import { useDispatchTypeLabel } from "~/hooks/language";
import { useLocation } from "~/hooks/locations";
import {
  useTimeSlotsForDispatchType,
  useWaitTimeForDispatchType,
} from "~/hooks/ordering";
import type { Location as Loc, OrderDispatchOption } from "~/types";

export default function NowOrLater() {
  const { locationRef, location, loading: loadingLocation } = useLocation();
  const searchParams = useSearchParams();
  const source = searchParams.get("source");
  const router = useRouter();

  const { dispatchOption, serviceRef, timeSlot, setOrderingOptions } =
    useCart(locationRef);

  const timeSlots = useTimeSlotsForDispatchType(locationRef, dispatchOption);

  const { label, loading: loadingLabel } = useDispatchTypeLabel(dispatchOption);

  const waitTime = useWaitTimeForDispatchType(locationRef, dispatchOption);

  const hours = useHours(dispatchOption);

  const goToASAP = () => {
    if (dispatchOption) setOrderingOptions(dispatchOption, null, serviceRef);

    if (source === "cart") {
      router.replace(`/${locationRef}/cart`);
      return;
    }

    router.replace(`/${locationRef}/menu`);
  };

  const goToLocation = () => router.replace(`/${locationRef}`);

  const goToTimeSlots = () => {
    if (dispatchOption)
      setOrderingOptions(dispatchOption, timeSlot, serviceRef);

    if (source) {
      router.replace(`/${locationRef}/when/later?source=${source}`);
      return;
    }

    router.replace(`/${locationRef}/when/later`);
  };

  const goToDeliveryAddress = () =>
    router.replace(`/${locationRef}/delivery-address`);

  const goToPrevious = () => {
    if (source === "cart") {
      router.replace(`/${locationRef}/cart`);
      return;
    }

    dispatchOption === "DELIVERY" ? goToDeliveryAddress() : goToLocation();
  };

  const loading = hours?.loading || loadingLabel || loadingLocation;

  return (
    <Modal title={label || ""} open={true} onClose={goToPrevious} isCloseBack>
      <Main
        loading={loading}
        loc={location}
        waitTime={waitTime}
        goToMenu={goToASAP}
        goToTimeSlots={goToTimeSlots}
        hasTimeSlots={Boolean(timeSlots?.length)}
        dispatchOption={dispatchOption}
        hours={hours}
      />
    </Modal>
  );
}

function Main({
  loading,
  loc,
  dispatchOption,
  hours,
  hasTimeSlots,
  waitTime,
  goToMenu,
  goToTimeSlots,
}: {
  loading: boolean;
  loc?: Loc;
  waitTime?: string | null;
  goToMenu: () => void;
  goToTimeSlots: () => void;
  hasTimeSlots: boolean;
  dispatchOption: OrderDispatchOption | null;
  hours: HoursResult | null;
}) {
  const asapSubTitle = formatSubTitle(
    dispatchOption,
    "asap",
    hours?.status === "closed",
    hasTimeSlots,
    waitTime,
  );

  const laterSubTitle = formatSubTitle(
    dispatchOption,
    "later",
    hours?.status === "closed",
    hasTimeSlots,
    waitTime,
  );

  return (
    <FadeIn transitionDuration={200} visible={true}>
      <Stack space={"4"}>
        <LocationPanel skeleton={loading} canChangeLocation={false} loc={loc} />

        <StoreHours skeleton={loading} type={dispatchOption} hours={hours} />

        <Text weight="semi-strong" size="large" font="accent">
          When do you want your order?
        </Text>

        <OptionButton
          title="Now"
          subTitle={asapSubTitle}
          icon="time-forward"
          onPress={goToMenu}
          loading={loading}
        />
        <OptionButton
          title="Schedule for Later"
          subTitle={laterSubTitle}
          icon="calendar"
          onPress={goToTimeSlots}
          disabled={!hasTimeSlots}
          loading={loading}
        />
      </Stack>
    </FadeIn>
  );
}

function formatSubTitle(
  dispatchOption: OrderDispatchOption | null,
  button: "asap" | "later",
  closed: boolean,
  hasTimeSlots: boolean,
  waitTime?: string | null,
) {
  switch (button) {
    case "asap":
      if (closed) {
        return "Currently unavailable";
      }
      if (dispatchOption === "DELIVERY") {
        return waitTime ? `Delivered in ${waitTime}` : "Delivered soon";
      }
      return waitTime ? `Ready in ${waitTime}` : "Ready soon";

    case "later":
      return hasTimeSlots ? "Choose date and time" : "Currently unavailable";
  }
}
